import { type IReactionDisposer, makeAutoObservable, reaction, runInAction } from 'mobx'
import type { ReactNode } from 'react'
import { type Blocker } from 'react-router'
import { isEqual } from 'lodash'
import { nanoid } from 'nanoid'
import { toastStore } from 'shared/ui'
import { uiStore } from 'shared/store/uiStore'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { ClickToTextApi } from 'entities/ClickToText/api/clickToText'
import { type IParamsClickToText, type IResponseClickToText } from 'entities/ClickToText/api/type'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { TagsDropdownControlStore } from 'widgets/TagsDropdown/store/tagsDropdownControlStore'
import { SendFromStore } from 'widgets/SendFrom'
import { ClickToTextModalContent } from 'pages/clickToText/pages/createClickToText/ui/ClickToTextModalContent'
import { type ClickToTextPageStore } from 'pages/clickToText'
import { EnumModeClickToText, type IClickToTextCreateField } from './type'
import { ClickToTextSMSStore } from './clickToTextSMSStore'

export class ClickToTextViewStore {
  private _isLoading = true
  private _isSaving = false
  private _name = ''
  private _errorsMap = new Map<IClickToTextCreateField, ReactNode>()
  private _isDragging = false
  private _cacheParams: IParamsClickToText | null = null
  private _modalKeepEditing = nanoid()
  private _modalDetails = nanoid()
  private _disposeIsAircallReaction: IReactionDisposer | null = null
  private _mergeFieldsProps = {
    isAllConnectedOrDisconnectedIntegration: false,
    isSelectDefault: true,
    useAllMergeFields: true,
  }
  private _mode: EnumModeClickToText = EnumModeClickToText.create
  private _editClickToText: IResponseClickToText | null = null
  private readonly _clickToTextSMSStore: ClickToTextSMSStore
  private readonly _sendFromStore: SendFromStore
  private _tagsControlStore = new TagsDropdownControlStore()

  constructor(
    private _entities: IEntitiesStore,
    private _clickToTextPageStore: ClickToTextPageStore | null,
    clickToTextResponse?: IResponseClickToText,
    isDuplicate?: boolean
  ) {
    makeAutoObservable(this)

    this._sendFromStore = new SendFromStore(this._entities, {
      excludeDeprioritizedNumbers: true,
      getDropdownItemsOptions: {
        excludeSendingOptions: true,
        showOnlyVerifiedToolFree: true,
      },
    })
    this._clickToTextSMSStore = new ClickToTextSMSStore(
      {
        onFocus: () => {
          this.removeError('message')
        },
        placeholder: 'Write your message...',
        mergeFieldsProps: this._mergeFieldsProps,
        isMarketing: true,
        actionsConfig: {
          showMedia: false,
          showSavedReplies: false,
          showEmoji: true,
          showAIAssistant: true,
          showMergeFields: false,
          showShortenLink: false,
        },
      },
      this._entities
    )
    void this.init(clickToTextResponse, isDuplicate)
  }

  init = async (clickToTextResponse?: IResponseClickToText, isDuplicate?: boolean) => {
    runInAction(() => {
      this._isLoading = true
    })

    await Promise.all([
      this._entities.integrationsStore.fetchIntegrations(),
      this._entities.mergeFieldStore.loadAllFields(),
      this._sendFromStore.init(),
    ])

    if (clickToTextResponse) {
      if (isDuplicate) {
        runInAction(() => {
          this._name = 'Copy of: ' + clickToTextResponse.name
        })
      } else {
        runInAction(() => {
          this._editClickToText = clickToTextResponse
          this._mode = EnumModeClickToText.edit
          this._name = clickToTextResponse.name
        })
      }

      if (clickToTextResponse.tags?.length) {
        this._tagsControlStore.init(clickToTextResponse.tags)
      }
      this._clickToTextSMSStore.init(clickToTextResponse)

      if (clickToTextResponse.number) {
        await this._sendFromStore.setSelectItem({
          number_id: clickToTextResponse.number.id,
        })
      }
    }

    if (!isDuplicate) {
      runInAction(() => {
        this._cacheParams = this.clickToTextParams
      })
    }

    this.reactionIsAircall()

    runInAction(() => {
      this._isLoading = false
    })
  }

  get sendFromStore() {
    return this._sendFromStore
  }

  get isLoading() {
    return this._isLoading
  }

  get name() {
    return this._name
  }

  get clickToTextSMSStore() {
    return this._clickToTextSMSStore
  }

  get isDraggingCondition() {
    return this._isDragging
  }

  get tagsControlStore() {
    return this._tagsControlStore
  }

  get errorsMap() {
    return this._errorsMap
  }

  get isSaving() {
    return this._isSaving
  }

  get clickToTextParams(): IParamsClickToText {
    const numberId = this._sendFromStore.number_id || 0
    const { message } = this.clickToTextSMSStore.messageDataParams
    return {
      name: this.name,
      number_id: numberId,
      tags: this.tagsControlStore.tags.map((tag) => tag.id),
      message,
    }
  }

  get isExistChanges() {
    return !isEqual(this._cacheParams, this.clickToTextParams)
  }

  get isEditMode() {
    return this._mode === EnumModeClickToText.edit
  }

  setName = (name: string) => {
    this._name = name
  }

  setError = (key: IClickToTextCreateField, error: ReactNode) => {
    if (key === 'numbers') {
      this._sendFromStore.setError(error as string)
    }
    this._errorsMap.set(key, error)
  }

  removeError = (key: IClickToTextCreateField) => {
    if (key === 'numbers') {
      this._sendFromStore.setError('')
    }
    this._errorsMap.delete(key)
  }

  setDraggingCondition = (condition: boolean) => {
    this._isDragging = condition
  }

  clearErrors = () => {
    this._sendFromStore.setError('')
    this.errorsMap.clear()
  }

  private saveClickToText = async (
    clickToTextParams: IParamsClickToText
  ): Promise<IResponseClickToText | null> => {
    const cacheParams = this._cacheParams
    try {
      runInAction(() => {
        this._isSaving = true
        this._cacheParams = clickToTextParams
      })

      let info: IResponseClickToText

      if (this.isEditMode && this._editClickToText) {
        const { data } = await ClickToTextApi.updateClickToText(
          this._editClickToText.id,
          clickToTextParams
        )
        info = data
      } else {
        const { data } = await ClickToTextApi.createClickToText(clickToTextParams)
        info = data
      }

      return info
    } catch (e) {
      this._cacheParams = cacheParams
      logger.error(e)
      return null
    } finally {
      runInAction(() => {
        this._isSaving = false
      })
    }
  }

  handleSaveClickToText = async () => {
    this.clearErrors()
    this.checkValidate()
    if (this.errorsMap.size || this.clickToTextSMSStore.isError) {
      return
    }

    const info = await this.saveClickToText(this.clickToTextParams)
    if (info) {
      uiStore.setRoutesBlocker(null)
      uiStore.changeRoute({ path: '/click-to-text' })

      setTimeout(() => {
        if (this.isEditMode) {
          toastStore.add({
            type: 'success',
            title: 'Updates saved',
          })
        } else {
          this.openDetailsModal(info)
        }
      }, 100)
    }
  }

  openDetailsModal = (data: IResponseClickToText) => {
    this._clickToTextPageStore?.setIsDetailsModalOpen(true)
    modalStore.addModal({
      id: this._modalDetails,
      title: 'Details',
      width: 360,
      type: ModalTypeList.DEFAULT,
      ModalContent: ClickToTextModalContent,
      ModalContentProps: data,
      pureContent: true,
      onClose: () => {
        this._clickToTextPageStore?.setIsDetailsModalOpen(false)
        modalStore.removeModal(this._modalDetails)
      },
    })
  }

  closeDetailsModal = () => {
    this._clickToTextPageStore?.setIsDetailsModalOpen(false)
    modalStore.removeModal(this._modalDetails)
  }

  checkValidate = () => {
    const { name, number_id, message } = this.clickToTextParams

    if (!name.trim().length) {
      this.setError('name', 'Please enter link name')
    }

    if (!number_id) {
      this.setError('numbers', 'Please select the inbox to send to')
    }

    if (!message) {
      this.setError('message', 'Your message is the key – please write it above')
    }
  }

  handleShowKeepEditingModal = (blocker: Blocker) => {
    if (blocker.state !== 'blocked') return

    modalStore.addModal({
      id: this._modalKeepEditing,
      type: ModalTypeList.INFO,
      title: 'Leaving will discard your content',
      primaryAction: {
        text: 'Keep editing',
        onAction: () => {
          modalStore.closeModal(this._modalKeepEditing)
        },
      },
      secondaryAction: {
        text: 'Close without saving',
        onAction: () => {
          const path = blocker.location.pathname
          blocker.proceed()

          uiStore.setRoutesBlocker(null)
          uiStore.changeRoute({
            path,
            type: 'vue',
            method: 'replace',
          })
          modalStore.closeModal(this._modalKeepEditing)
        },
      },
    })
  }

  reset = () => {
    this._disposeIsAircallReaction?.()
  }

  reactionIsAircall = () => {
    this._disposeIsAircallReaction?.()
    this._disposeIsAircallReaction = reaction(
      () => this._sendFromStore.selectedDropdownItem?.data?.number?.is_aircall,
      (isAircall) => {
        this._clickToTextSMSStore.messageFieldStore.setIsCurrentAirCall(!!isAircall)
      }
    )
  }
}
